// skipcq: JS-W1028, JS-0128
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Link from '@mui/material/Link';
import ScrapedBeerResults from './ScrapedBeerResults';
import ImportSummaryView from './ImportSummaryView';
import SearchResultsList from './SearchResultsList';
import {
  canImportScrapedBeers,
  extractBeersFromScrapingResult,
} from '../../../utils/beerStyle/scrapedBeerImport';

/**
 * ScrapingStatusView
 * Displays scraping status with URL and loading indicator.
 */
function ScrapingStatusView({ scrapingUrl, scrapingError }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "200px"
      }}>
      <CircularProgress size={40} />
      <Typography variant="body1" sx={{ mt: 3, mb: 1 }}>
        Scraping beer data...
      </Typography>
      <Link
        href={scrapingUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ wordBreak: 'break-word', textAlign: 'center' }}
      >
        {scrapingUrl}
      </Link>
      {scrapingError && (
        <Box sx={{
          mt: 2
        }}>
          <Typography variant="body2" color="error">
            {scrapingError}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

ScrapingStatusView.propTypes = {
  scrapingUrl: PropTypes.string.isRequired,
  scrapingError: PropTypes.string,
};

ScrapingStatusView.defaultProps = {
  scrapingError: null,
};

/**
 * SearchLoadingView
 * Displays search loading state.
 */
function SearchLoadingView() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "200px"
      }}>
      <CircularProgress />
    </Box>
  );
}

/**
 * SearchErrorView
 * Displays search error message.
 */
function SearchErrorView({ searchError }) {
  return (
    <Box sx={{
      mt: 2
    }}>
      <Typography variant="body2" color="error">
        {searchError}
      </Typography>
    </Box>
  );
}

SearchErrorView.propTypes = {
  searchError: PropTypes.string.isRequired,
};

/**
 * SearchResultsView
 * Displays search results with scraping status overlay.
 */
function SearchResultsView({
  searchResults,
  scrapingLoading,
  scrapingUrl,
  scrapingError,
  onSelectClick,
  onLinkClick,
}) {
  return (
    <>
      <SearchResultsList
        searchResults={searchResults}
        scrapingLoading={scrapingLoading}
        scrapingUrl={scrapingUrl}
        onSelectClick={onSelectClick}
        onLinkClick={onLinkClick}
      />
      {scrapingLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 2
          }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Scraping beer data...
          </Typography>
        </Box>
      )}
      {scrapingError && (
        <Box sx={{
          mt: 2
        }}>
          <Typography variant="body2" color="error">
            {scrapingError}
          </Typography>
        </Box>
      )}
    </>
  );
}

SearchResultsView.propTypes = {
  searchResults: PropTypes.object.isRequired,
  scrapingLoading: PropTypes.bool.isRequired,
  scrapingUrl: PropTypes.string,
  scrapingError: PropTypes.string,
  onSelectClick: PropTypes.func.isRequired,
  onLinkClick: PropTypes.func.isRequired,
};

SearchResultsView.defaultProps = {
  scrapingUrl: null,
  scrapingError: null,
};

/**
 * ScrapedResultsView
 * Displays scraped beer results.
 */
function ScrapedResultsView({
  scrapingResult,
  selectedUrl,
  importError,
  styleMapping,
  styleTypes,
  styleTypeByName,
  onAssignStyleType,
  onSelectionChange,
}) {
  const companyName =
    scrapingResult?.json?.company_name ||
    scrapingResult?.data?.json?.company_name ||
    '';
  const companyDescription =
    scrapingResult?.json?.company_description ||
    scrapingResult?.data?.json?.company_description ||
    '';
  const companyAddress =
    scrapingResult?.json?.company_address ||
    scrapingResult?.data?.json?.company_address ||
    '';

  const beersArrayFromJson = scrapingResult?.json?.beers;
  const beersArrayFromData = scrapingResult?.data?.json?.beers;
  const beers = useMemo(() => {
    return Array.isArray(beersArrayFromJson)
      ? beersArrayFromJson
      : Array.isArray(beersArrayFromData)
        ? beersArrayFromData
        : [];
  }, [beersArrayFromJson, beersArrayFromData]);

  return (
    <Box>
      <ScrapedBeerResults
        companyName={companyName}
        companyDescription={companyDescription}
        companyAddress={companyAddress}
        beers={beers}
        selectedUrl={selectedUrl}
        importError={importError}
        styleMapping={styleMapping}
        styleTypes={styleTypes}
        styleTypeByName={styleTypeByName}
        onAssignStyleType={onAssignStyleType}
        onSelectionChange={onSelectionChange}
      />
    </Box>
  );
}

ScrapedResultsView.propTypes = {
  scrapingResult: PropTypes.object.isRequired,
  selectedUrl: PropTypes.string,
  importError: PropTypes.string,
  styleMapping: PropTypes.object,
  styleTypes: PropTypes.arrayOf(PropTypes.object),
  styleTypeByName: PropTypes.object,
  onAssignStyleType: PropTypes.func,
  onSelectionChange: PropTypes.func,
};

ScrapedResultsView.defaultProps = {
  selectedUrl: null,
  importError: null,
  styleMapping: {},
  styleTypes: [],
  styleTypeByName: {},
  onAssignStyleType: null,
  onSelectionChange: null,
};

/**
 * FindBeerDialogContent
 * Renders the appropriate content view based on current state.
 */
function FindBeerDialogContent({
  showImportSummary,
  importResults,
  searchLoading,
  searchError,
  searchResults,
  scrapingLoading,
  scrapingUrl,
  scrapingError,
  scrapingResult,
  selectedUrl,
  importError,
  styleMapping,
  styleTypes,
  styleTypeByName,
  onAssignStyleType,
  onSelectClick,
  onLinkClick,
  onSelectionChange,
}) {
  if (showImportSummary) {
    return <ImportSummaryView importResults={importResults} />;
  }

  if (scrapingResult) {
    return (
      <ScrapedResultsView
        scrapingResult={scrapingResult}
        selectedUrl={selectedUrl}
        importError={importError}
        styleMapping={styleMapping}
        styleTypes={styleTypes}
        styleTypeByName={styleTypeByName}
        onAssignStyleType={onAssignStyleType}
        onSelectionChange={onSelectionChange}
      />
    );
  }

  if (scrapingLoading && scrapingUrl) {
    return (
      <ScrapingStatusView scrapingUrl={scrapingUrl} scrapingError={scrapingError} />
    );
  }

  if (searchLoading) {
    return <SearchLoadingView />;
  }

  if (searchError) {
    return <SearchErrorView searchError={searchError} />;
  }

  if (searchResults) {
    return (
      <SearchResultsView
        searchResults={searchResults}
        scrapingLoading={scrapingLoading}
        scrapingUrl={scrapingUrl}
        scrapingError={scrapingError}
        onSelectClick={onSelectClick}
        onLinkClick={onLinkClick}
      />
    );
  }

  return null;
}

FindBeerDialogContent.propTypes = {
  showImportSummary: PropTypes.bool.isRequired,
  importResults: PropTypes.object,
  searchLoading: PropTypes.bool.isRequired,
  searchError: PropTypes.string,
  searchResults: PropTypes.object,
  scrapingLoading: PropTypes.bool.isRequired,
  scrapingUrl: PropTypes.string,
  scrapingError: PropTypes.string,
  scrapingResult: PropTypes.object,
  selectedUrl: PropTypes.string,
  importError: PropTypes.string,
  styleMapping: PropTypes.object,
  styleTypes: PropTypes.arrayOf(PropTypes.object),
  styleTypeByName: PropTypes.object,
  onAssignStyleType: PropTypes.func,
  onSelectClick: PropTypes.func.isRequired,
  onLinkClick: PropTypes.func.isRequired,
  onSelectionChange: PropTypes.func,
};

FindBeerDialogContent.defaultProps = {
  importResults: null,
  searchError: null,
  searchResults: null,
  scrapingUrl: null,
  scrapingError: null,
  scrapingResult: null,
  selectedUrl: null,
  importError: null,
  styleMapping: {},
  styleTypes: [],
  styleTypeByName: {},
  onAssignStyleType: null,
  onSelectionChange: null,
};

/**
 * FindBeerDialog
 * Dialog component for finding and importing beers.
 */
function FindBeerDialog({
  open,
  onClose,
  showImportSummary,
  searchLoading,
  searchError,
  searchResults,
  scrapingLoading,
  scrapingUrl,
  scrapingError,
  scrapingResult,
  selectedUrl,
  importLoading,
  catalogLoading = false,
  importError,
  importResults,
  styleMapping,
  styleTypes,
  styleTypeByName,
  selectedBeerIndices,
  onAssignStyleType,
  onSelectClick,
  onLinkClick,
  onImportBeers,
  onSelectionChange,
}) {
  const hasValidBeers = useMemo(() => {
    const scrapedBeers = extractBeersFromScrapingResult(scrapingResult);
    return canImportScrapedBeers(
      scrapedBeers,
      selectedBeerIndices,
      styleMapping,
      styleTypeByName,
    );
  }, [scrapingResult, selectedBeerIndices, styleMapping, styleTypeByName]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="find-beer-dialog-title"
      aria-describedby="find-beer-dialog-description"
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="find-beer-dialog-title">
        {showImportSummary ? 'Import Results' : 'Find Beer'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          id="find-beer-dialog-description"
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {showImportSummary
            ? 'View the results of importing beers from a web source.'
            : 'Search for and import beers from web sources.'}
        </DialogContentText>
        <FindBeerDialogContent
          showImportSummary={showImportSummary}
          importResults={importResults}
          searchLoading={searchLoading}
          searchError={searchError}
          searchResults={searchResults}
          scrapingLoading={scrapingLoading}
          scrapingUrl={scrapingUrl}
          scrapingError={scrapingError}
          scrapingResult={scrapingResult}
          selectedUrl={selectedUrl}
          importError={importError}
          styleMapping={styleMapping}
          styleTypes={styleTypes}
          styleTypeByName={styleTypeByName}
          onAssignStyleType={onAssignStyleType}
          onSelectClick={onSelectClick}
          onLinkClick={onLinkClick}
          onSelectionChange={onSelectionChange}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {showImportSummary
            ? 'Done'
            : scrapingResult
              ? 'Close'
              : 'Cancel'}
        </Button>
        {scrapingResult && !showImportSummary && hasValidBeers && (
          <Button
            onClick={onImportBeers}
            color="primary"
            variant="contained"
            disabled={importLoading || catalogLoading}
            startIcon={
              importLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {importLoading ? 'Importing...' : 'Import Beer'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

FindBeerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  showImportSummary: PropTypes.bool.isRequired,
  searchLoading: PropTypes.bool.isRequired,
  searchError: PropTypes.string,
  searchResults: PropTypes.object,
  scrapingLoading: PropTypes.bool.isRequired,
  scrapingUrl: PropTypes.string,
  scrapingError: PropTypes.string,
  scrapingResult: PropTypes.object,
  selectedUrl: PropTypes.string,
  importLoading: PropTypes.bool.isRequired,
  catalogLoading: PropTypes.bool,
  importError: PropTypes.string,
  importResults: PropTypes.object,
  styleMapping: PropTypes.object.isRequired,
  styleTypes: PropTypes.arrayOf(PropTypes.object),
  styleTypeByName: PropTypes.object,
  selectedBeerIndices: PropTypes.arrayOf(PropTypes.number),
  onAssignStyleType: PropTypes.func,
  onSelectClick: PropTypes.func.isRequired,
  onLinkClick: PropTypes.func.isRequired,
  onImportBeers: PropTypes.func.isRequired,
  onSelectionChange: PropTypes.func,
};

FindBeerDialog.defaultProps = {
  searchError: null,
  searchResults: null,
  scrapingUrl: null,
  scrapingError: null,
  scrapingResult: null,
  selectedUrl: null,
  catalogLoading: false,
  importError: null,
  importResults: null,
  styleTypes: [],
  styleTypeByName: {},
  selectedBeerIndices: [],
  onAssignStyleType: null,
  onSelectionChange: null,
};

export default FindBeerDialog;
